import React, { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import { AuditFormData, CreateAuditProps } from "../../types/api";
import {
  useDepartmentStore,
  useFireSafetyAudits,
  useOrgUsersStore,
  usePremisesStore,
  useRoleManageStore,
} from "../../store/store";

const CreateAudits: React.FC<CreateAuditProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isView = false,
  isEdit = false,
  initialData,
  moduleName,
}) => {
  const { allPremises, fetchPremises } = usePremisesStore();
  const { allChickList, getAllChecklist, updateAudit } = useFireSafetyAudits();
  const { departments, fetchAllDepartments } = useDepartmentStore();
  const { rolesData, fetchAllRoles } = useRoleManageStore();
  // Add this new state for filtered auditor users
  const [auditorUsers, setAuditorUsers] = useState<any[]>([]);
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();

  useEffect(() => {
    fetchAllDepartments();
    fetchOrgUsers();
    getAllChecklist(moduleName);
    fetchAllRoles();
  }, [
    fetchAllDepartments,
    getAllChecklist,
    fetchOrgUsers,
    fetchAllRoles,
    moduleName,
  ]);

  useEffect(() => {
    if (rolesData && orgUsersData) {
      const auditorRole = rolesData.find(
        (role: any) => role.roleName === "auditor"
      );

      if (auditorRole) {
        const filteredAuditorUsers = orgUsersData.filter(
          (user: any) => user.role === auditorRole._id
        );
        setAuditorUsers(filteredAuditorUsers);
      }
    }
  }, [rolesData, orgUsersData]);

  const defaultFormData: AuditFormData = {
    _id: "",
    premise: "",
    auditFrequency: "",
    startDate: "",
    reminder: "",
    checklist: [],
    assignedAuditors: "",
    department: [],
    auditType: "",
    schedule: "",
    moduleName: moduleName || "",
    audit_status: "Scheduled",
  };

  const [formData, setFormData] = useState<AuditFormData>(defaultFormData);
  useEffect(() => {
    if (initialData && (isEdit || isView)) {
      setFormData(initialData);
    } else if (!isEdit) {
      setFormData(defaultFormData);
    }
  }, [initialData, isEdit, isView, isOpen]);
  const [dropdownStates, setDropdownStates] = useState({
    premise: false,
    auditFrequency: false,
    reminder: false,
    checklist: false,
    department: false,
    assignedAuditors: false,
    auditType: false,
    schedule: false,
  });
  useEffect(() => {
    fetchPremises();
  }, [fetchPremises]);

  // Mock data - replace with actual data from your stores/APIs
  const mockOptions = {
    frequencies: ["Daily", "Weekly", "Monthly", "Quarterly", "Annually"],
    reminders: [
      "1 day before",
      "3 days before",
      "1 week before",
      "2 weeks before",
    ],
    auditTypes: ["Internal", "External", "Mock Drill-linked ", "Authority"],
    schedules: [
      "Morning (9 AM - 12 PM)",
      "Afternoon (1 PM - 5 PM)",
      "Evening (6 PM - 9 PM)",
      "Full Day",
    ],
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
  };

  const handleCancel = () => {
    setFormData(defaultFormData);
    setDropdownStates({
      premise: false,
      auditFrequency: false,
      reminder: false,
      checklist: false,
      assignedAuditors: false,
      department: false,
      auditType: false,
      schedule: false,
    });
    onClose();
  };

  const toggleDropdown = (field: keyof typeof dropdownStates) => {
    if (isView) return; // Prevent dropdown opening in view mode
    setDropdownStates((prev) => ({
      ...Object.keys(prev).reduce(
        (acc, key) => ({
          ...acc,
          [key]: false,
        }),
        {} as typeof prev
      ),
      [field]: !prev[field],
    }));
  };

  const handleSingleSelect = (
    field:
      | "premise"
      | "auditFrequency"
      | "auditType"
      | "assignedAuditors"
      | "reminder"
      | "schedule",
    value: string
  ) => {
    if (isView) return; // Prevent changes in view mode
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setDropdownStates((prev) => ({
      ...prev,
      [field]: false,
    }));
  };

  const handleMultiSelect = (
    field: "checklist" | "department",
    value: string
  ) => {
    if (isView) return; // Prevent changes in view mode
    setFormData((prev) => {
      const currentValues = prev[field] as string[];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((item) => item !== value)
        : [...currentValues, value];
      return {
        ...prev,
        [field]: newValues,
      };
    });
  };

  const renderSingleSelectDropdown = (
    field:
      | "premise"
      | "auditFrequency"
      | "auditType"
      | "assignedAuditors"
      | "reminder"
      | "schedule",
    placeholder: string,
    options: string[]
  ) => {
    if (field === "premise") {
      const selectedPremise = allPremises.find(
        (p) => (p._id || p.id) === formData.premise
      );
      const displayValue = selectedPremise ? selectedPremise.premisesName : "";
      return (
        <div className="relative">
          <button
            type="button"
            onClick={() => !isView && toggleDropdown(field)}
            disabled={isView}
            value={selectedPremise?.premisesName}
            className={`w-full px-4 py-3 border border-gray-300 rounded-lg text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
              isView ? "bg-gray-100 cursor-not-allowed" : "bg-white"
            }`}
          >
            <span className={displayValue ? "text-gray-900" : "text-gray-500"}>
              {displayValue || placeholder}
            </span>
            {!isView && <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {dropdownStates[field] && !isView && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
              {allPremises.map((premise) => (
                <button
                  key={premise._id || premise.id}
                  type="button"
                  onClick={() =>
                    handleSingleSelect(field, premise._id || premise.id)
                  }
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
                >
                  {premise.premisesName}
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (field === "assignedAuditors") {
      const selectedAuditor = auditorUsers.find(
        (auditor) => auditor._id === formData.assignedAuditors
      );
      const displayValue = selectedAuditor
        ? `${selectedAuditor.firstName || ""} ${
            selectedAuditor.lastName || ""
          }`.trim() ||
          selectedAuditor.username ||
          ""
        : "";
      return (
        <div className="relative">
          <button
            type="button"
            onClick={() => !isView && toggleDropdown(field)}
            disabled={isView}
            className={`w-full px-4 py-3 border border-gray-300 rounded-lg text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
              isView ? "bg-gray-100 cursor-not-allowed" : "bg-white"
            }`}
          >
            <span className={displayValue ? "text-gray-900" : "text-gray-500"}>
              {displayValue || placeholder}
            </span>
            {!isView && <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {dropdownStates[field] && !isView && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
              {auditorUsers.map((auditor) => (
                <button
                  key={auditor._id}
                  type="button"
                  onClick={() => handleSingleSelect(field, auditor._id)}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
                >
                  {`${auditor.firstName || ""} ${
                    auditor.lastName || ""
                  }`.trim() ||
                    auditor.username ||
                    ""}
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Default handling for other fields
    return (
      <div className="relative">
        <button
          type="button"
          onClick={() => !isView && toggleDropdown(field)}
          disabled={isView}
          className={`w-full px-4 py-3 border border-gray-300 rounded-lg text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
            isView ? "bg-gray-100 cursor-not-allowed" : "bg-white"
          }`}
        >
          <span className={formData[field] ? "text-gray-900" : "text-gray-500"}>
            {formData[field] || placeholder}
          </span>
          {!isView && <ChevronDown className="w-5 h-5 text-gray-400" />}
        </button>

        {dropdownStates[field] && !isView && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            {options.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => handleSingleSelect(field, option)}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
              >
                {option}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderMultiSelectDropdown = (
    field: "checklist" | "department",
    placeholder: string,
    options: string[]
  ) => {
    if (field === "checklist") {
      const selectedChecklists = formData.checklist;
      const displayValue =
        selectedChecklists.length > 0
          ? `${selectedChecklists.length} selected`
          : "";
      return (
        <div className="relative">
          <button
            type="button"
            onClick={() => !isView && toggleDropdown(field)}
            disabled={isView}
            className={`w-full px-4 py-3 border border-gray-300 rounded-lg text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
              isView ? "bg-gray-100 cursor-not-allowed" : "bg-white"
            }`}
          >
            <span className={displayValue ? "text-gray-900" : "text-gray-500"}>
              {displayValue || placeholder}
            </span>
            {!isView && <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {dropdownStates[field] && !isView && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
              {allChickList
                .filter((checklist) => checklist.moduleName === moduleName)
                .map((checklist) => (
                  <label
                    key={checklist._id}
                    className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedChecklists.includes(checklist._id)}
                      onChange={() => handleMultiSelect(field, checklist._id)}
                      className="mr-3 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <span className="text-gray-900">{checklist.checkList}</span>
                  </label>
                ))}
            </div>
          )}
        </div>
      );
    } else if (field === "department") {
      const selectedDepartments = departments.filter(
        (dept) => dept._id && formData.department.includes(dept._id)
      );
      const displayValue =
        selectedDepartments.length > 0
          ? `${selectedDepartments.length} selected`
          : "";

      return (
        <div className="relative">
          <button
            type="button"
            onClick={() => !isView && toggleDropdown(field)}
            disabled={isView}
            className={`w-full px-4 py-3 border border-gray-300 rounded-lg text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
              isView ? "bg-gray-100 cursor-not-allowed" : "bg-white"
            }`}
          >
            <span className={displayValue ? "text-gray-900" : "text-gray-500"}>
              {displayValue || placeholder}
            </span>
            {!isView && <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {dropdownStates[field] && !isView && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
              {departments
                .filter((dept) => dept._id)
                .map((dept) => (
                  <label
                    key={dept._id!}
                    className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.department.includes(dept._id!)}
                      onChange={() => handleMultiSelect(field, dept._id!)}
                      className="mr-3 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <span className="text-gray-900">{dept.departmentName}</span>
                  </label>
                ))}
            </div>
          )}
        </div>
      );
    }

    // Default handling for other fields
    return (
      <div className="relative">
        <button
          type="button"
          onClick={() => !isView && toggleDropdown(field)}
          disabled={isView}
          className={`w-full px-4 py-3 border border-gray-300 rounded-lg text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
            isView ? "bg-gray-100 cursor-not-allowed" : "bg-white"
          }`}
        >
          {(() => {
            const selected =
              field === "checklist" ? formData.checklist : formData.department;
            return (
              <span
                className={
                  selected.length > 0 ? "text-gray-900" : "text-gray-500"
                }
              >
                {selected.length > 0
                  ? `${selected.length} selected`
                  : placeholder}
              </span>
            );
          })()}
          {!isView && <ChevronDown className="w-5 h-5 text-gray-400" />}
        </button>

        {dropdownStates[field] && !isView && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            {options.map((option) => (
              <label
                key={option}
                className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={(field === "checklist"
                    ? formData.checklist
                    : formData.department
                  ).includes(option)}
                  onChange={() => handleMultiSelect(field, option)}
                  className="mr-3 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                />
                <span className="text-gray-900">{option}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
    >
      <div className="bg-white w-full max-w-5xl max-h-[90vh] rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            {isEdit ? "Edit Audit" : isView ? "View Audit" : "Create Audit"}
          </h2>
        </div>

        {/* Form Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Premise
                </label>
                {renderSingleSelectDropdown("premise", "Select Premise", [])}
              </div>

              {/* Audit Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Audit Frequency
                </label>
                {renderSingleSelectDropdown(
                  "auditFrequency",
                  "Select Frequency",
                  mockOptions.frequencies
                )}
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      startDate: e.target.value,
                    }))
                  }
                  disabled={isView}
                  className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    isView ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reminder
                </label>
                {renderSingleSelectDropdown(
                  "reminder",
                  "Select Reminder",
                  mockOptions.reminders
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Check List
                </label>
                {renderMultiSelectDropdown("checklist", "Select Checklist", [])}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned Auditor
                </label>
                {renderSingleSelectDropdown(
                  "assignedAuditors",
                  "Select Auditor",
                  []
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Department
                </label>
                {renderMultiSelectDropdown(
                  "department",
                  "Select Department",
                  departments
                    .filter((dept) => dept._id)
                    .map((dept) => dept._id!)
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Audit Type
                </label>
                {renderSingleSelectDropdown(
                  "auditType",
                  "Select Audit Type",
                  mockOptions.auditTypes
                )}
              </div>

              {/* Schedule */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Schedule
                </label>
                {renderSingleSelectDropdown(
                  "schedule",
                  "Select Schedule",
                  mockOptions.schedules
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-2 bg-[#61748e] border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleCancel}
            className="flex items-center justify-center font-semibold gap-3 bg-[#869ab6] text-white px-4 py-2 rounded-full"
          >
            CANCEL
          </button>
          {isView ? null : (
            <button
              type="submit"
              onClick={async (e) => {
                if (isEdit) {
                  if (!moduleName) {
                    toast.error("Module name is required");
                    return;
                  }
                  try {
                    await updateAudit(
                      formData?._id || "",
                      formData,
                      moduleName
                    );
                    onClose();
                  } catch (error) {
                    console.error("Error updating audit:", error);
                  }
                } else {
                  handleSubmit(e);
                }
              }}
              className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
            >
              {isEdit ? "UPDATE" : "SUBMIT"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateAudits;
